import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, LogOut, Paperclip, X, ChevronDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getToken = () => sessionStorage.getItem('vp_admin_token');
const getSessionId = () => {
  let sid = sessionStorage.getItem('vp_chat_session');
  if (!sid) { sid = `session-${Date.now()}`; sessionStorage.setItem('vp_chat_session', sid); }
  return sid;
};

const SUGGESTIONS = [
  'Show me all jar products',
  'Show all contact form leads',
  'Create an exhibition popup for Cosmoprof Mumbai',
  'Show current site settings',
  'List all active popups',
];

export default function AdminChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [adminEmail, setAdminEmail] = useState('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const sessionId = getSessionId();

  // Verify auth + load history
  useEffect(() => {
    const token = getToken();
    if (!token) { navigate('/admin/login'); return; }
    fetch(`${API}/api/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setAdminEmail(d.email))
      .catch(() => navigate('/admin/login'));

    fetch(`${API}/api/admin/chat/history/${sessionId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(history => {
        if (Array.isArray(history) && history.length > 0) {
          const formatted = history.map(m => ({
            role: m.role,
            content: m.content,
            action_results: m.action_results,
            timestamp: m.timestamp,
          }));
          setMessages(formatted);
        } else {
          setMessages([{
            role: 'assistant',
            content: "Welcome to Vimal Plastics Admin Panel. I can help you manage products, create homepage popups, update site settings, and view leads. What would you like to do?",
          }]);
        }
      })
      .catch(() => {
        setMessages([{
          role: 'assistant',
          content: "Welcome to Vimal Plastics Admin Panel. I can help you manage products, create homepage popups, update site settings, and view leads. What would you like to do?",
        }]);
      });
  }, [navigate, sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setUploadedFile(null);
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      if (res.status === 401) { navigate('/admin/login'); return; }
      const data = await res.json();
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        action_results: data.action_results,
      }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  }, [loading, navigate, sessionId]);

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API}/api/admin/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setUploadedFile(data);
      setInput(`I've uploaded image "${data.filename}" (${data.size_kb}KB). Please assign it to `);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Image upload failed: ${err.message}` }]);
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('vp_admin_token');
    sessionStorage.removeItem('vp_chat_session');
    navigate('/admin/login');
  };

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: '#0B1B3D' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-3">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-medium" style={{ color: '#D4AF37' }}>Vimal Plastics</p>
            <p className="text-sm text-white font-light">Admin Panel</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <p className="text-xs hidden sm:block" style={{ color: 'rgba(255,255,255,0.4)' }}>{adminEmail}</p>
          <button onClick={handleLogout} className="flex items-center gap-1.5 text-xs transition-opacity hover:opacity-70" style={{ color: 'rgba(255,255,255,0.5)' }} data-testid="admin-logout-btn">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-5">
        {messages.map((msg, i) => (
          <MessageBubble key={msg.id || `${msg.role}-${msg.timestamp || i}`} msg={msg} />
        ))}
        {loading && (
          <div className="flex gap-3 items-start">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#D4AF37' }}>
              <span className="text-[10px] font-bold text-navy" style={{ color: '#0B1B3D' }}>VP</span>
            </div>
            <div className="px-4 py-3 rounded-lg max-w-[80%]" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
              <div className="flex gap-1">
                {[0,1,2].map(n => <span key={n} className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ backgroundColor: '#D4AF37', animationDelay: `${n*0.15}s` }} />)}
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions (only show when no user messages yet) */}
      {messages.filter(m => m.role === 'user').length === 0 && (
        <div className="px-4 pb-3 flex flex-wrap gap-2">
          {SUGGESTIONS.map(s => (
            <button
              key={s}
              onClick={() => sendMessage(s)}
              className="text-xs px-3 py-1.5 rounded-full transition-all hover:opacity-80"
              style={{ backgroundColor: 'rgba(212,175,55,0.12)', color: '#D4AF37', border: '1px solid rgba(212,175,55,0.25)' }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="px-4 pb-5 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        {uploadedFile && (
          <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded" style={{ backgroundColor: 'rgba(212,175,55,0.1)' }}>
            <span className="text-xs" style={{ color: '#D4AF37' }}>Image ready: {uploadedFile.filename} ({uploadedFile.size_kb}KB)</span>
            <button onClick={() => { setUploadedFile(null); setInput(''); }}><X size={12} style={{ color: '#D4AF37' }} /></button>
          </div>
        )}
        <div className="flex items-end gap-3 pt-3">
          <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={e => handleImageUpload(e.target.files[0])} />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex-shrink-0 p-2.5 rounded transition-opacity hover:opacity-70 disabled:opacity-40"
            style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
            title="Upload product image"
            data-testid="admin-upload-btn"
          >
            <Paperclip size={16} style={{ color: 'rgba(255,255,255,0.5)' }} />
          </button>
          <div className="flex-1 flex items-end rounded" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
              placeholder="Type a command... e.g. 'Add a popup for our exhibition on 20 March'"
              className="flex-1 px-4 py-3 text-sm bg-transparent text-white outline-none resize-none"
              style={{ color: 'rgba(255,255,255,0.9)', minHeight: '44px', maxHeight: '120px' }}
              rows={1}
              data-testid="admin-chat-input"
            />
          </div>
          <button
            onClick={() => sendMessage(input)}
            disabled={loading || !input.trim()}
            className="flex-shrink-0 p-2.5 rounded transition-all disabled:opacity-30 hover:opacity-85"
            style={{ backgroundColor: '#D4AF37' }}
            data-testid="admin-send-btn"
          >
            <Send size={16} style={{ color: '#0B1B3D' }} />
          </button>
        </div>
        <p className="text-center mt-2 text-xs" style={{ color: 'rgba(255,255,255,0.15)' }}>Enter to send · Shift+Enter for new line</p>
      </div>
    </div>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  const [showResults, setShowResults] = useState(false);
  const hasResults = msg.action_results && msg.action_results.length > 0;

  return (
    <div className={`flex gap-3 items-start ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold"
        style={{ backgroundColor: isUser ? 'rgba(255,255,255,0.1)' : '#D4AF37', color: isUser ? 'rgba(255,255,255,0.7)' : '#0B1B3D' }}
      >
        {isUser ? 'You' : 'VP'}
      </div>

      <div className={`max-w-[78%] ${isUser ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
        {/* Message */}
        <div
          className="px-4 py-3 text-sm leading-relaxed"
          style={{
            backgroundColor: isUser ? 'rgba(212,175,55,0.15)' : 'rgba(255,255,255,0.06)',
            color: 'rgba(255,255,255,0.88)',
            borderRadius: isUser ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
          }}
        >
          {msg.content}
        </div>

        {/* Action results toggle */}
        {hasResults && (
          <button
            onClick={() => setShowResults(!showResults)}
            className="flex items-center gap-1 text-xs transition-opacity hover:opacity-80"
            style={{ color: 'rgba(212,175,55,0.7)' }}
          >
            <ChevronDown size={12} style={{ transform: showResults ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            {showResults ? 'Hide' : 'Show'} details
          </button>
        )}

        {showResults && (
          <div className="w-full rounded p-3 text-xs overflow-auto max-h-64" style={{ backgroundColor: 'rgba(0,0,0,0.3)', color: 'rgba(255,255,255,0.6)' }}>
            <pre className="whitespace-pre-wrap">{JSON.stringify(msg.action_results, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
}
